/**
 * Admin Dashboard Page
 * Overview metrics for the admin panel
 */

import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    People as PeopleIcon,
    PersonPin as AdminIcon,
    CheckCircle as ActiveIcon,
    Category as CategoryIcon,
} from '@mui/icons-material';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { api } from '@shared/api/client';

interface StatCard {
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    bg: string;
}

function MetricCard({ title, value, icon, color, bg }: StatCard) {
    return (
        <Card
            elevation={0}
            sx={{
                border: '1px solid rgba(4, 55, 123, 0.15)',
                borderRadius: 2,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 4px 20px rgba(4, 55, 123, 0.12)' },
            }}
        >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 3 }}>
                <Box
                    sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color,
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#04377b', lineHeight: 1 }}>
                        {value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {title}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export function AdminDashboardPage() {
    const [stats, setStats] = useState({ total: 0, active: 0, admins: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function loadStats() {
            try {
                setLoading(true);
                // Fetch total users
                const allRes = await api.listUsers({ page: 1, size: 1 } as any);
                const total = (allRes.data?.data as any)?.total ?? 0;

                // Fetch active users
                const activeRes = await api.listUsers({ page: 1, size: 1, is_active: true } as any);
                const active = (activeRes.data?.data as any)?.total ?? 0;

                // Fetch admin users
                const adminRes = await api.listUsers({ page: 1, size: 1, role: 'admin' } as any);
                const admins = (adminRes.data?.data as any)?.total ?? 0;

                setStats({ total, active, admins });
            } catch (err) {
                setError('Failed to load admin statistics. Please try again.');
            } finally {
                setLoading(false);
            }
        }
        loadStats();
    }, []);

    return (
        <AuthenticatedLayout>
            <Box>


                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#04377b' }} />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="Total Users"
                                value={stats.total}
                                icon={<PeopleIcon />}
                                color="#04377b"
                                bg="rgba(4, 55, 123, 0.08)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="Active Users"
                                value={stats.active}
                                icon={<ActiveIcon />}
                                color="#2e7d32"
                                bg="rgba(46, 125, 50, 0.08)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="Admin Users"
                                value={stats.admins}
                                icon={<AdminIcon />}
                                color="#8B0000"
                                bg="rgba(139, 0, 0, 0.08)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="Regular Users"
                                value={stats.total - stats.admins}
                                icon={<CategoryIcon />}
                                color="#e65100"
                                bg="rgba(230, 81, 0, 0.08)"
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>
        </AuthenticatedLayout>
    );
}
